// operations.js
'use strict';
import { fromJS } from 'immutable';
import {
  MERGE_OPERATION_DATA,
  REMOVE_OPERATION_DATA,
  SET_OPERATION_DATA,
  PUSH_OPERATION_DATA,
  RESET_OPERATION_DATA,
  RESTORE_OPERATION_DATA,
  CLEAR_OPERATION_DATA,
  // -- PLOP_PREPEND_REDUCER_ACTION_TYPE --
  LOGOUT,
} from '../ActionTypes.js';

import { RANGE_24H } from '../resource/leaderboardConstants.js';
import { CONTENT_SIZE_SELECT_PATH_PREFIX } from '../resource/popupNotificationConstants.js';
import {
  showcaseMobileWidth,
  showcaseMobileMinimumItemAmount,
} from '../style/variables.js';

const appVersion = process.env.TAG_NAME || process.env.BRANCH_NAME || 'local';
const appSha = process.env.SHORT_SHA;

const isServer = typeof window === 'undefined';

const hostname = isServer ? 'swag.live' : location.hostname;
const notificationPermission = isServer
  ? 'default'
  : window.Notification?.permission || 'default';
const defaultPreferences = /^men/i.test(hostname)
  ? ['gender:male']
  : ['gender:female'];

export const defaultState = fromJS({
  drm: {},
  permission: {
    notification: notificationPermission,
  },
  bindEmail: {},
  login: {
    username: '',
    password: '',
    shouldKeepUsername: null,
    isLoginJustCompleted: false,
  },
  register: {
    username: '',
    password: '',
    usernameValidation: '',
    passwordValidation: '',
    hasAgeChecked: null,
    preferences: defaultPreferences,
    birthdateUnix: null,
    isRegisterJustCompleted: false,
    hasShownWelcomeToastr: false,
  },
  journal: {
    category: 'all',
  },
  message: {},
  toastr: {
    stacks: [],
  },
  locationOptions: {
    all: [],
    select: [],
  },
  mobileAppDownloader: {
    linkData: [],
  },
  myProfileInfoDraft: {},
  myProfileList: {},
  player: {
    volume: 1,
    isMuted: true,
    isFirstUnmuted: true,
    isDragToMute: false,
    volumeBeforeDragToMute: null,
  },
  leaderboard: {
    category: '',
  },
  revenue: {
    analytics: {
      selectedTab: 'daily',
    },
    fanLeaderboard: {
      range: RANGE_24H,
    },
  },
  serviceWorker: {},
  // data from pusher channel 'private-happy-hour'
  freeZone: {},
  showcase: {
    activeIndex: 0,
    itemAmount: showcaseMobileMinimumItemAmount,
    itemWidth: showcaseMobileWidth,
  },
  config: {
    language: 'en',
  },
  liveStream: {
    enabledLowLatency: null,
    enabledSuperChatConfirmationModal: true,
    isNeedSendGiftConfirm: true,
    isNeedTriggerExclusiveConfirm: true,
    isNeedTriggerGroupConfirm: true,
    paid: {},
    autoPayment: {},
    shouldShowRetryPlayButton: false,
    panel: {
      isGiftTooltipVisible: false,
    },
    shouldDisplayCleanModeHint: true,
    shouldDisplayExploreMoreHint: true,
    shouldDisplayHorizonLayoutHint: true,
    shouldDisplayAsideModuleHint: true,
    shouldDisplayExclusiveRevisionTooltip: true,
  },
  footerNavigation: {},
  popupNotification: {
    size: {},
  },
  device: {},
  prepaid: {},
  jobs: {},
  search: {
    storyRenderAmount: 10,
  },
  acquisition: {
    manual: {},
    referral: {},
    prepaid: {},
  },
  pusher: {
    channels: {},
    connection: {},
    inViewportUserIds: [],
  },
  visibility: {
    isBrowserTabVisible: true,
  },
  webapp: {
    version: appVersion,
    shortSha: appSha,
    timestamp: 0,
  },
  engagement: {},
});

/**
 * Operations.
 * @module reducer/operations
 */
const operations = (state = defaultState, action) => {
  switch (action.type) {
    case MERGE_OPERATION_DATA:
      return _MERGE_OPERATION_DATA(action.payload)(state);
    case REMOVE_OPERATION_DATA:
      return _REMOVE_OPERATION_DATA(action.payload)(state);
    case SET_OPERATION_DATA:
      return _SET_OPERATION_DATA(action.payload)(state);
    case PUSH_OPERATION_DATA:
      return _PUSH_OPERATION_DATA(action.payload)(state);
    case RESET_OPERATION_DATA:
      return _RESET_OPERATION_DATA(action.payload)(state);
    case RESTORE_OPERATION_DATA:
      return _RESTORE_OPERATION_DATA(action.payload)(state);
    // -- PLOP_PREPEND_REDUCER_SWITCH_CASE --
    case LOGOUT:
      return _LOGOUT(action.payload)(state);
    case CLEAR_OPERATION_DATA:
      return _CLEAR_OPERATION_DATA(action.payload)(state);
    default:
      return state;
  }
};

/**
 * Merge operation data
 * @kind reducer/actionType
 * @name MERGE_OPERATION_DATA
 * @param {array} {selectPath} - operation data select path
 * @param {any} {data} - operation data
 * @return {Immutable.Map} New state
 */
const _MERGE_OPERATION_DATA =
  ({ selectPath, data }) =>
  state => {
    return state.mergeDeepIn(selectPath, data);
  };

/**
 * Remove operation data
 * @kind reducer/actionType
 * @name REMOVE_OPERATION_DATA
 * @param {array} {selectPath} - operation data select path
 * @return {Immutable.Map} New state
 */
const _REMOVE_OPERATION_DATA =
  ({ selectPath }) =>
  state => {
    return state
      .deleteIn(selectPath)
      .mergeDeepIn(selectPath, defaultState.getIn(selectPath));
  };

/**
 * Update operation data
 * @kind reducer/actionType
 * @name SET_OPERATION_DATA
 * @param {array} selectPath - operation data select path
 * @param {any} data - operation data
 * @return {Immutable.Map} New state
 */
const _SET_OPERATION_DATA =
  ({ selectPath, data }) =>
  state => {
    return state.setIn([...selectPath], fromJS(data));
  };

/**
 * Push operation data
 * @kind reducer/actionType
 * @name PUSH_OPERATION_DATA
 * @param {array} selectPath - operation data select path
 * @param {any} data - operation data
 * @return {Immutable.Map} New state
 */
const _PUSH_OPERATION_DATA =
  ({ selectPath, data }) =>
  state => {
    return state.setIn(
      [...selectPath],
      (state.getIn([...selectPath]) || fromJS([])).push(fromJS(data))
    );
  };

/**
 * Clear flix editor
 * @kind reducer/actionType
 * @name RESET_OPERATION_DATA
 * @param {array} selectPath - operation data select path
 * @return {Immutable.Map} New state
 */
const _RESET_OPERATION_DATA =
  ({ selectPath }) =>
  state => {
    return state.setIn(selectPath, defaultState.getIn(selectPath));
  };

/**
 * Restore operation data
 * @kind reducer/actionType
 * @name RESTORE_OPERATION_DATA
 * @param {object} operationData - operation data
 * @return {Immutable.Map} New state
 */
const _RESTORE_OPERATION_DATA = operationData => state => {
  return state.mergeDeep(operationData);
};

/**
 * Clear operation data
 * @kind reducer/actionType
 * @return {Immutable.Map} New state
 */
const _CLEAR_OPERATION_DATA = () => () => {
  return defaultState;
};

// -- PLOP_PREPEND_REDUCER_ACTION_HANDLER --

/**
 * Logout
 * @kind reducer/actionType
 * @name LOGOUT
 * @return {Immutable.Map} New state
 */
const _LOGOUT = () => state => {
  return defaultState.mergeDeep({
    login: {
      entry: state.getIn(['login', 'entry']),
      otp: state.getIn(['login', 'otp']),
    },
    mobileAppDownloader: state.get('mobileAppDownloader'),
    permission: state.get('permission'),
    freeZone: state.get('freeZone').delete('coolAtUnix'),
    serviceWorker: state.get('serviceWorker'),
    showcase: state.get('showcase').merge({
      activeIndex: 0,
    }),
    urlPopup: state.get('urlPopup'),
    acquisition: state.get('acquisition'),
    jobs: state.get('jobs'),
    ab: state.get('ab'),
    config: state
      .get('config')
      .delete('currentConfigKey')
      .delete('remoteConfigMerged'),
    device: state.get('device'),
    footerNavigation: state.get('footerNavigation'),
    popupNotification: {
      size: state.getIn([...CONTENT_SIZE_SELECT_PATH_PREFIX]),
    },
    backpack: state.get('backpack'),
    missions: state.get('missions'),
    liveStream: {
      enabledLowLatency: state.getIn(['liveStream', 'enabledLowLatency']),
    },
    intercom: {
      canDisplayIntercom: state.getIn(['intercom', 'canDisplayIntercom']),
    },
    search: {
      storyRenderAmount: state.getIn(['search', 'storyRenderAmount']),
    },
    pusher: state.get('pusher'),
    join: {
      draft: state.getIn(['join', 'draft']) || {},
    },
    welcomeBack: state.get('welcomeBack'),
  });
};

export default operations;
